import { create } from 'zustand';
import { feedbackService } from 'src/services/feedback';

type FeedbackView = 'rating' | 'excellent' | 'feedback' | 'feedback-thanks';

interface FeedbackModalState {
  isOpen: boolean;
  rating: number;
  hoveredRating: number;
  feedbackText: string;
  view: FeedbackView;
  isSubmitting: boolean;
  canShow: boolean;
  checkAndOpen: () => Promise<void>;
  close: () => void;
  setRating: (rating: number) => void;
  setHoveredRating: (rating: number) => void;
  setFeedbackText: (text: string) => void;
  submitRating: () => void;
  submitFeedback: () => Promise<void>;
  reset: () => void;
}

const FEEDBACK_MAX_LENGTH = 256;

export { FEEDBACK_MAX_LENGTH };

export const useFeedbackModalStore = create<FeedbackModalState>((set, get) => ({
  isOpen: false,
  rating: 0,
  hoveredRating: 0,
  feedbackText: '',
  view: 'rating',
  isSubmitting: false,
  canShow: false,

  checkAndOpen: async () => {
    try {
      const enabled = await feedbackService.isEnabled();
      if (!enabled) return;
      set({ canShow: true, isOpen: true });
    } catch {
      // on error, don't show the modal
    }
  },

  close: () => set({ isOpen: false }),

  setRating: (rating) => set({ rating }),
  setHoveredRating: (hoveredRating) => set({ hoveredRating }),
  setFeedbackText: (feedbackText) => {
    if (feedbackText.length <= FEEDBACK_MAX_LENGTH) {
      set({ feedbackText });
    }
  },

  submitRating: async () => {
    const { rating, feedbackText } = get();
    set({ isSubmitting: true });
    try {
      await feedbackService.submit(
        rating,
        feedbackText.trim() || undefined
      );
      if (rating === 5) {
        set({ isSubmitting: false, view: 'excellent' });
      } else {
        set({ isSubmitting: false, view: 'feedback-thanks' });
      }
    } catch {
      set({ isSubmitting: false, view: 'feedback-thanks' });
    }
  },

  submitFeedback: async () => {
    const { rating, feedbackText } = get();
    set({ isSubmitting: true });
    try {
      await feedbackService.submit(rating, feedbackText.trim() || undefined);
      set({ isSubmitting: false, view: 'feedback-thanks' });
    } catch {
      set({ isSubmitting: false, view: 'feedback-thanks' });
    }
  },

  reset: () =>
    set({
      isOpen: false,
      rating: 0,
      hoveredRating: 0,
      feedbackText: '',
      view: 'rating',
      isSubmitting: false,
      canShow: false,
    }),
}));
